import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaAngleRight, FaPhone, FaEnvelope, FaMapMarkerAlt, FaHeart, FaWhatsapp, FaShieldAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import TripproLogo from "../assests/tripproLogo.png";

// Custom hook to track window width
const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowWidth;
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

// Container for the entire footer
const FooterContainer = styled.footer`
  width: 100%;
  background: linear-gradient(135deg, #02495b 0%, #035e75 50%, #02495b 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1CA8CB, #02495b, #1CA8CB);
  }
`;

// Top wave decoration
const WaveDecoration = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' fill-opacity='0.03' d='M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z'%3E%3C/path%3E%3C/svg%3E") no-repeat;
  background-size: cover;
  opacity: 0.5;
`;

// Main content wrapper
const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem 1.5rem;
  }
  @media (max-width: 480px) {
    padding: 2rem 1rem 1rem;
  }
`;

// A flex container that wraps its children
const FooterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

// Each column in the footer
const FooterCol = styled.div`
  padding: 1rem 0;
`;

// Logo section
const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 576px) {
    align-items: center;
  }
`;

// Logo image styles  
const LogoImage = styled.img`
  width: 120px;
  height: auto;
  object-fit: contain;
  margin-bottom: 1rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 100px;
  }
`;

// Brand description
const BrandDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.7;
  margin: 0;
  max-width: 280px;

  @media (max-width: 576px) {
    text-align: center;
    max-width: 100%;
  }
`;

// Registered business badge
const RegisteredBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 1rem;
  padding: 10px 14px;
  background: rgba(28, 168, 203, 0.15);
  border-radius: 8px;
  border: 1px solid rgba(28, 168, 203, 0.3);

  svg {
    color: #1CA8CB;
    font-size: 16px;
  }

  span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
    font-weight: 500;
  }

  @media (max-width: 576px) {
    justify-content: center;
  }
`;

// Heading for each footer column
const FooterHeading = styled.h4`
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-size: 18px;
  font-weight: 600;
  position: relative;
  font-family: 'Merriweather', serif;
  letter-spacing: 0.5px;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, #1CA8CB, transparent);
    border-radius: 2px;
  }

  @media (max-width: 576px) {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

// List for quick links
const QuickLinksList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

// Individual quick link item
const QuickLinkItem = styled.li`
  margin-bottom: 12px;
`;

// Quick link anchor styling
const QuickLink = styled.a`
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;

  &::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: #1CA8CB;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #1CA8CB;
    transform: translateX(5px);

    &::before {
      width: 100%;
    }
  }

  svg {
    transition: transform 0.3s ease;
    color: #1CA8CB;
    font-size: 12px;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @media (max-width: 576px) {
    justify-content: center;
  }
`;

// Router Link styled
const QuickRouterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;

  &::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: #1CA8CB;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #1CA8CB;
    transform: translateX(5px);

    &::before {
      width: 100%;
    }
  }

  svg {
    transition: transform 0.3s ease;
    color: #1CA8CB;
    font-size: 12px;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @media (max-width: 576px) {
    justify-content: center;
  }
`;

// Contact item styling
const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(5px);
  }

  @media (max-width: 576px) {
    justify-content: center;
  }
`;

const ContactIcon = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(28, 168, 203, 0.15);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1CA8CB;
  font-size: 14px;
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${ContactItem}:hover & {
    background: #1CA8CB;
    color: white;
  }
`;

const WhatsAppIcon = styled(ContactIcon)`
  background: rgba(37, 211, 102, 0.15);
  color: #25D366;

  ${ContactItem}:hover & {
    background: #25D366;
    color: white;
  }
`;

const ContactText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.5;

  strong {
    display: block;
    color: #ffffff;
    font-size: 13px;
    margin-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

// Social Media icons container
const SocialMediaIcons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 0.5rem;

  @media (max-width: 576px) {
    justify-content: center;
  }
`;

// Link wrapping each social media icon
const SocialMediaLink = styled.a`
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: #1CA8CB;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(28, 168, 203, 0.3);
    border-color: transparent;
  }
`;

// Social Media icon image styles
const SocialMediaIcon = styled.img`
  width: 22px;
  height: 22px;
  transition: transform 0.3s ease;

  ${SocialMediaLink}:hover & {
    transform: scale(1.1);
  }
`;

// Divider line
const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  margin: 2rem 0;
`;

// Footer bottom section styling
const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0;
`;

// Policy Links
const PolicyLinks = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const PolicyLink = styled(Link)`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #1CA8CB;
  }
`;

// Developer credit container
const DeveloperCredit = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  margin: 0;

  svg {
    color: #ff6b6b;
    animation: ${pulse} 1.5s ease-in-out infinite;
  }

  a {
    color: #1CA8CB;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
      color: #ffffff;
    }
  }
`;

const Footer = () => {
  const windowWidth = useWindowWidth();

  const quickLinks = [
    { name: "Home", link: "/" },
    { name: "About Us", link: "/about" },
    { name: "Destinations", link: "/destination" },
    { name: "Blog", link: "/blog" },
    { name: "Contact Us", link: "/contact" },
    { name: "Career", link: "/career" },
  ];

  const socialMediaLinks = [
    {
      link: "https://www.instagram.com/trippro.in/?igshid=ZDdkNTZiNTM%3D",
      img: "https://cdn-icons-png.freepik.com/256/2111/2111463.png",
      alt: "Instagram",
    },
    {
      link: "https://www.facebook.com/people/Trippro/100089604003210/?mibextid=ZbWKwL",
      img: "https://cdn-icons-png.freepik.com/256/2504/2504903.png",
      alt: "Facebook",
    },
    {
      link: "https://www.linkedin.com/company/92498979/admin/dashboard/",
      img: "https://cdn-icons-png.freepik.com/256/3536/3536505.png",
      alt: "LinkedIn",
    },
    {
      link: "https://www.youtube.com/@Trippro-social",
      img: "https://cdn-icons-png.freepik.com/256/3938/3938026.png",
      alt: "YouTube",
    },
  ];

  const handleWhatsApp = () => {
    window.open("https://wa.me/919211376008?text=Hi%20Trippro!%20I%20need%20help%20with%20my%20travel%20plans.", "_blank");
  };

  return (
    <FooterContainer>
      <WaveDecoration />
      <FooterContent>
        <FooterRow>
          {/* Logo Section */}
          <FooterCol>
            <LogoSection>
              <LogoImage src={TripproLogo} alt="Trippro Logo" />
              <BrandDescription>
                Your trusted travel partner for unforgettable adventures. We create personalized journeys that turn your travel dreams into reality.
              </BrandDescription>
              <RegisteredBadge>
                <FaShieldAlt />
                <span>Trippro is a registered travel business.</span>
              </RegisteredBadge>
            </LogoSection>
          </FooterCol>

          {/* Quick Links */}
          <FooterCol>
            <FooterHeading>Quick Links</FooterHeading>
            <QuickLinksList>
              {quickLinks.map((item, index) => (
                <QuickLinkItem key={index}>
                  <QuickLink href={item.link}>
                    <FaAngleRight />
                    {item.name}
                  </QuickLink>
                </QuickLinkItem>
              ))}
            </QuickLinksList>
          </FooterCol>

          {/* Get In Touch */}
          <FooterCol>
            <FooterHeading>Get In Touch</FooterHeading>
            <ContactItem onClick={() => (window.location.href = "tel:+919211375008")}>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactText>
                <strong>Phone</strong>
                +91 92113 75008
              </ContactText>
            </ContactItem>
            <ContactItem onClick={handleWhatsApp}>
              <WhatsAppIcon>
                <FaWhatsapp />
              </WhatsAppIcon>
              <ContactText>
                <strong>WhatsApp</strong>
                +91 92113 76008
              </ContactText>
            </ContactItem>
            <ContactItem onClick={() => (window.location.href = "mailto:contact@trippro.in")}>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactText>
                <strong>Email</strong>
                contact@trippro.in
              </ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>
                <strong>Address</strong>
                Trippro, 104, Ajay Park,<br />Najafgarh, Delhi
              </ContactText>
            </ContactItem>
          </FooterCol>

          {/* Social Media */}
          <FooterCol>
            <FooterHeading>Follow Us</FooterHeading>
            <BrandDescription style={{ marginBottom: '1rem' }}>
              Stay connected and get the latest travel updates and offers.
            </BrandDescription>
            <SocialMediaIcons>
              {socialMediaLinks.map(({ link, img, alt }, index) => (
                <SocialMediaLink key={index} href={link} target="_blank" rel="noopener noreferrer" title={alt}>
                  <SocialMediaIcon src={img} alt={alt} />
                </SocialMediaLink>
              ))}
            </SocialMediaIcons>
          </FooterCol>
        </FooterRow>

        <Divider />

        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} Trippro. All rights reserved.
          </Copyright>
          <PolicyLinks>
            <PolicyLink to="/cancellation-refund">Simple booking & cancellation policy. No hidden charges.</PolicyLink>
          </PolicyLinks>
          <DeveloperCredit>
            Made with <FaHeart /> by{" "}
            <a href="https://netzwert.tech" target="_blank" rel="noopener noreferrer">
              Netzwert Technologies
            </a>
          </DeveloperCredit>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;